import { useTheme } from '@material-ui/core';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { RepairerSearchItem } from 'app/core/api/repairer/api-repairer.types';
import styledConsts from 'app/styles/styledConsts';
import React, { useEffect, useState } from 'react';
import { GeocodeCoords } from 'react-geocode';
import { useDebouncedCallback } from 'use-debounce';

const coloredMarker = (color: string, size = 1): google.maps.Icon => {
  const baseW = 22;
  const baseH = 30;
  const w = baseW * size;
  const h = baseH * size;
  return {
    url: `data:image/svg+xml,${encodeURIComponent(
      [
        `<svg viewBox="0 0 ${baseW} ${baseH}" xmlns="http://www.w3.org/2000/svg">`,
        `<path d="m 12.5,27.5 c -0.5,2.5 -2.5,2.5 -3,0 C 8.5,21.5 1,18 1,11.5 v 0 c 0,-5 4.5,-10 10,-10 5.5,0 10,5 10,10 0,6.5 -7.5,10 -8.5,16 z" fill="${color}" stroke="#ffffff" stroke-width="1"/>`,
        '<circle cx="11" cy="11.5" r="3" fill="#ffffff"/>',
        '</svg>',
      ].join('')
    )}`,
    anchor: new window.google.maps.Point(w / 2, h),
    scaledSize: new window.google.maps.Size(w, h),
  };
};

const adjustCoords = (
  position: GeocodeCoords,
  delta: GeocodeCoords
) => ({
  lat: position.lat + delta.lat,
  lng: position.lng + delta.lng,
});

export type MapProps = {
  repairers: RepairerSearchItem[];
  selectedRepairerId?: number;
  onRepairerSelected: (repairer: RepairerSearchItem) => void;
  onMapDragged: (location: GeocodeCoords) => void;
  currentLocation: GeocodeCoords;
};
function MapComponent({
  repairers,
  selectedRepairerId,
  onRepairerSelected,
  onMapDragged,
  currentLocation,
}: MapProps): JSX.Element {
  const theme = useTheme();
  const [map, setMap] = useState<google.maps.Map>();
  const [mapPosition, setMapPosition] =
    useState<GeocodeCoords>(currentLocation);

  const mapLoaded = !!map;
  useEffect(() => {
    if (mapLoaded) {
      setMapPosition({
        lat: repairers?.length
          ? repairers[0].latitude
          : currentLocation.lat,
        lng: repairers?.length
          ? repairers[0].longitude
          : currentLocation.lng,
      });
    }
  }, [repairers, currentLocation, mapLoaded]);

  const defaultMapOptions = {
    fullscreenControl: false,
    zoomControl: false,
    mapTypeControl: false,
    streetViewControl: false,
  };

  const onLoad = (mapProp: google.maps.Map) => {
    const milesW = 5;
    const coordsW = milesW / 60;
    const bounds = new window.google.maps.LatLngBounds(
      adjustCoords(currentLocation, { lat: -coordsW, lng: -coordsW }),
      adjustCoords(currentLocation, { lat: coordsW, lng: coordsW })
    );
    mapProp.fitBounds(bounds);
    setMap(mapProp);
  };

  const onUnmount = () => setMap(undefined);

  const handleMapMoved = () => {
    const loc = map?.getCenter();
    if (loc) {
      const coords = { lat: loc.lat(), lng: loc.lng() };
      onMapDragged(coords);
      setMapPosition(coords);
    }
  };
  const handleMapMovedDebounced = useDebouncedCallback(
    handleMapMoved,
    1000
  );
  return (
    <GoogleMap
      mapContainerStyle={{ flex: '1 1 auto', width: '100%' }}
      options={defaultMapOptions}
      onLoad={onLoad}
      onUnmount={onUnmount}
      center={mapPosition}
      onZoomChanged={handleMapMovedDebounced}
      onDragEnd={handleMapMoved}
    >
      {repairers?.map(repairer => (
        <Marker
          position={{
            lat: repairer.latitude,
            lng: repairer.longitude,
          }}
          key={repairer.id}
          onClick={() => onRepairerSelected(repairer)}
          icon={coloredMarker(
            styledConsts.cError({ theme }),
            repairer.id === selectedRepairerId ? 1.4 : 1.1
          )}
        />
      ))}
    </GoogleMap>
  );
}

export const MapView = React.memo(MapComponent);
